import React, { useMemo } from 'react';
import { Text, HoverCard, ActionIcon, Group, Stack } from '@mantine/core';
import { IconInfoCircle, IconStarFilled } from '@tabler/icons-react';
import { PropagationStopper, When } from '../common';
import { StoreNumberBadge } from './StoreNumberBadge';

import type { StoreWithNumber } from '../../pages/price-surveys/types';

interface SurveyZoneStoreCountProps {
    stores: StoreWithNumber[];
    championStoreId?: string | null;
    dropdownZIndex?: number;
}

function storeRowKey(store: StoreWithNumber, index: number): string {
    return store.id ?? store.storeNo ?? store.name ?? `store-${index}`;
}

export function SurveyZoneStoreCount({
    stores,
    championStoreId,
    dropdownZIndex = 2500,
}: SurveyZoneStoreCountProps) {
    const storesForList = useMemo(() => {
        const list = stores || [];
        return list.filter(
            store => !!store?.storeNo?.trim() || !!store?.name?.trim(),
        );
    }, [stores]);

    const storeCount = storesForList.length;

    return (
        <Group gap={4}>
            <Text size="sm" c="dimmed">
                {storeCount} {storeCount === 1 ? 'Store' : 'Stores'}
            </Text>
            <HoverCard
                withArrow
                arrowOffset={6}
                arrowSize={10}
                position="bottom-start"
                offset={10}
                withinPortal
                floatingStrategy="fixed"
                middlewares={{
                    flip: false,
                    shift: { padding: 8 },
                }}
                shadow="md"
                zIndex={dropdownZIndex}
                openDelay={200}
                closeDelay={400}
                transitionProps={{
                    transition: 'scale',
                    duration: 300,
                }}
                classNames={{
                    dropdown:
                        'bg-white shadow-md border border-gray-200 max-w-[min(100vw-2rem,38rem)] max-h-[min(70vh,32rem)] rounded-md overflow-y-auto overflow-x-hidden',
                }}
            >
                <HoverCard.Target>
                    <PropagationStopper>
                        <ActionIcon
                            variant="transparent"
                            color="gray"
                            size="xs"
                            aria-label="View all stores in this zone"
                        >
                            <IconInfoCircle size={14} />
                        </ActionIcon>
                    </PropagationStopper>
                </HoverCard.Target>
                <HoverCard.Dropdown>
                    <When
                        condition={storesForList.length > 0}
                        fallback={
                            <Text size="sm" p={4}>
                                No stores available
                            </Text>
                        }
                    >
                        <Stack
                            gap="sm"
                            p="xs"
                            className={
                                storeCount === 1
                                    ? 'max-w-[min(100vw-2rem,38rem)]'
                                    : 'min-w-[220px] max-w-[min(100vw-2rem,38rem)]'
                            }
                        >
                            {storesForList.map((store, index) => {
                                const isChampion =
                                    !!championStoreId &&
                                    !!store.id &&
                                    championStoreId === store.id;
                                const trimmedStoreNo = store.storeNo?.trim();
                                const hasStoreNo = !!trimmedStoreNo;
                                const hasName = !!store.name?.trim();

                                return (
                                    <Group
                                        key={storeRowKey(store, index)}
                                        gap="sm"
                                        wrap="nowrap"
                                        align="center"
                                        justify="flex-start"
                                        className="w-full"
                                    >
                                        {hasName ? (
                                            <Text
                                                size="sm"
                                                c="dark.6"
                                                className={
                                                    storeCount === 1
                                                        ? 'min-w-0 truncate'
                                                        : 'min-w-0 flex-1 truncate'
                                                }
                                            >
                                                {store.name!.trim()}
                                            </Text>
                                        ) : null}
                                        {hasStoreNo ? (
                                            <StoreNumberBadge
                                                storeNo={trimmedStoreNo}
                                                isChampion={isChampion}
                                            />
                                        ) : null}
                                        {!hasStoreNo && isChampion ? (
                                            <IconStarFilled
                                                size={14}
                                                className="flex-shrink-0 text-yellow-500"
                                                aria-label="Champion store"
                                            />
                                        ) : null}
                                    </Group>
                                );
                            })}
                        </Stack>
                    </When>
                </HoverCard.Dropdown>
            </HoverCard>
        </Group>
    );
}
